import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import uuid from 'react-uuid';
import type { OPMCalculator } from 'api/models/OPMCalculator';
import { valuationsAction } from 'common/actions';
import { VALIDATED } from 'common/constants/general';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { ValuationByAllocation } from 'common/types/valuation/valuation';
import { Tab } from 'components';
import { getNumberValue, getStringValue } from 'utilities';
import type { OPMCalculatorInputs, OPMCalculatorPrefillSource } from '../../approaches/OPMCalculator/types';
import { OPM_CALCULATOR_MODEL_NAME } from '../../common/constants/valuations';
import { VALUATION_SUMMARY_KEY } from '../../common/constants/valuationSummary';
import { valuationTabId } from '../../common/utils/valuationTabId';
import { toOPMDate } from '../../components/ShowOPMCalculationsLink/readOPMCalculatorInputs';
import useOPMCalculatorDecimalPlaces from '../../components/ShowOPMCalculationsLink/useOPMCalculatorDecimalPlaces';
import type { OPMCalculatorDecimalPlaces } from '../../components/ShowOPMCalculationsLink/useOPMCalculatorDecimalPlaces';
import { getOPMCalculatorPanelId } from '../../util/getOPMCalculatorPanelId';
import { useApproachQueryParam } from '../useApproachQueryParam';
import { UseCreateOPMCalculatorParams } from '../useCreateValuation/types';
import { useOPMCalculatorUrlState } from '../useOPMCalculatorUrlState';
import type { OPMCalculatorUrlInputs } from '../useOPMCalculatorUrlState';

/**
 * The `OPMCalculator` fields for a set of calculator inputs. Only the supplied keys are written,
 * so a maturity edit does not blank the volatility; a supplied `undefined` clears its field.
 */
const buildOPMCalculatorInputsPatch = (inputs: Partial<OPMCalculatorInputs>): Partial<OPMCalculator> => {
  const patch: Partial<OPMCalculator> = {};
  if ('capTableId' in inputs) {
    patch.cap_table = inputs.capTableId ?? null;
  }
  if ('equityValue' in inputs) {
    patch.equity_value = inputs.equityValue === undefined ? null : String(inputs.equityValue);
  }
  if ('riskFreeRate' in inputs) {
    patch.risk_free_rate = inputs.riskFreeRate === undefined ? null : String(inputs.riskFreeRate);
  }
  if ('maturity' in inputs) {
    patch.maturity = inputs.maturity === undefined ? null : String(inputs.maturity);
  }
  if ('volatility' in inputs) {
    patch.volatility = inputs.volatility === undefined ? null : String(inputs.volatility);
  }
  if ('opmDate' in inputs) {
    patch.opm_date = inputs.opmDate ?? null;
  }
  return patch;
};

/** A number rounded to its `OPMCalculator` column's precision, or null when it is unset. */
const toComparableNumber = (value: unknown, decimalPlaces?: number): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  return decimalPlaces === undefined ? parsed : Number(parsed.toFixed(decimalPlaces));
};

/**
 * The fields a calculator row is compared on, normalized: the API returns decimals as padded
 * strings (`'1000.00'`) while a local edit writes `'1000'`, and both mean the same input.
 */
const getComparableOPMCalculatorFields = (
  opmCalculator: Partial<OPMCalculator>,
  decimalPlaces: OPMCalculatorDecimalPlaces
) => ({
  name: getStringValue(opmCalculator.name),
  capTableId: toComparableNumber(opmCalculator.cap_table),
  equityValue: toComparableNumber(opmCalculator.equity_value, decimalPlaces.equityValue),
  riskFreeRate: toComparableNumber(opmCalculator.risk_free_rate, decimalPlaces.riskFreeRate),
  maturity: toComparableNumber(opmCalculator.maturity, decimalPlaces.maturity),
  volatility: toComparableNumber(opmCalculator.volatility, decimalPlaces.volatility),
  opmDate: toOPMDate(opmCalculator.opm_date) ?? null,
});

const hasOPMCalculatorChanged = (
  saved: Partial<OPMCalculator>,
  current: Partial<OPMCalculator>,
  decimalPlaces: OPMCalculatorDecimalPlaces
) => {
  const savedFields = getComparableOPMCalculatorFields(saved, decimalPlaces);
  const currentFields = getComparableOPMCalculatorFields(current, decimalPlaces);
  return (Object.keys(savedFields) as Array<keyof typeof savedFields>).some(
    key => savedFields[key] !== currentFields[key]
  );
};

/**
 * Whether a calculator already prices the linked inputs, so the link can focus it instead of
 * opening a duplicate. The risk-free rate only counts when the source supplied one: otherwise it
 * is derived from maturity, which is already compared. The OPM date always counts: the same
 * inputs at another date accrue dividends to another exit date, so they are another ladder.
 */
const pricesSameOPMInputs = (
  opmCalculator: OPMCalculator,
  inputs: Partial<OPMCalculatorUrlInputs>,
  decimalPlaces: OPMCalculatorDecimalPlaces
) => {
  const rowFields = getComparableOPMCalculatorFields(opmCalculator, decimalPlaces);
  const linkedFields = getComparableOPMCalculatorFields(
    buildOPMCalculatorInputsPatch({
      capTableId: inputs.capTableId,
      equityValue: inputs.equityValue,
      riskFreeRate: inputs.riskFreeRate,
      maturity: inputs.maturity,
      volatility: inputs.volatility,
      opmDate: inputs.opmDate,
    }),
    decimalPlaces
  );
  const keys = ['capTableId', 'equityValue', 'maturity', 'volatility', 'opmDate'] as const;

  return (
    keys.every(key => rowFields[key] === linkedFields[key])
    && (inputs.riskFreeRate === undefined || rowFields.riskFreeRate === linkedFields.riskFreeRate)
  );
};

/** The row's inputs in the calculator's URL shape, every key present so stale params clear. */
const getOPMCalculatorUrlInputs = (
  opmCalculator: OPMCalculator,
  decimalPlaces: OPMCalculatorDecimalPlaces
): OPMCalculatorUrlInputs => {
  const { capTableId, equityValue, riskFreeRate, maturity, volatility, opmDate } = getComparableOPMCalculatorFields(
    opmCalculator,
    decimalPlaces
  );
  return {
    capTableId: capTableId ?? undefined,
    equityValue: equityValue ?? undefined,
    riskFreeRate: riskFreeRate ?? undefined,
    maturity: maturity ?? undefined,
    volatility: volatility ?? undefined,
    opmDate: opmDate ?? undefined,
  };
};

/** Saved rows by pk. Rows added this session have no pk yet and are left out. */
const indexOPMCalculatorsById = (opmCalculators?: OPMCalculator[] | null) =>
  new Map(
    (opmCalculators ?? [])
      .filter(opmCalculator => opmCalculator.id)
      .map(opmCalculator => [getNumberValue(opmCalculator.id), opmCalculator])
  );

const useCreateOPMCalculator = (params: UseCreateOPMCalculatorParams) => {
  const {
    valuation,
    arePendingChanges,
    REDIRECT_TIMEOUT_DELAY,
    setValuation,
    setPageStatus,
    setArePendingChanges,
    setCurrentValuation,
    setIsEditingApproachName,
    setIsEditingCalibrationName,
    setIsEditingRangeSensitivityName,
    setIsEditingOPMCalculatorName,
    setIsChangingFinancialStatement,
    handleMenuClose,
    setApproachToUpdate,
    handleOpenUpdateApproachDialog,
    setCalibrationToUpdate,
    setRangeSensitivityToUpdate,
  } = params;
  const [, dispatch] = useStore() as unknown as UseStoreValues;
  const [opmCalculatorToUpdate, setOPMCalculatorToUpdate] = useState<OPMCalculator | null>(null);
  const [opmCalculatorTabs, setOPMCalculatorTabs] = useState<Tab[]>([]);
  const [opmCalculatorPrefillSource, setOPMCalculatorPrefillSource] = useState<OPMCalculatorPrefillSource | null>(
    null
  );

  const { setApproachQueryParam } = useApproachQueryParam();
  const { openCalculatorTab } = useOPMCalculatorUrlState();
  const opmCalculatorDecimalPlaces = useOPMCalculatorDecimalPlaces();

  /**
   * Latest-valuation ref.
   *
   * `handleUpdateOPMCalculatorInputs` is also called from an awaited continuation: editing
   * Maturity fires the FRED lookup, and the risk-free rate lands one round trip later through
   * the closure captured at the keystroke. That closure predates the maturity write, so
   * patching a captured `valuation` made the late rate write revert the very edit that asked
   * for it -- the row saved with a risk-free rate and a null maturity, and the calculator then
   * had incomplete inputs and rendered no results. Always patch the current valuation.
   */
  const valuationRef = useRef(valuation);
  valuationRef.current = valuation;

  /**
   * The calculators as last loaded or saved, by pk, so each tab can show whether it has changes
   * to save. Only refreshed while nothing is pending: that is the state right after a load or a
   * successful Save, and any local edit flips `arePendingChanges` in the same update.
   */
  // Seeded from the loaded valuation so saved tabs do not flash `*` on the first render.
  const [savedOPMCalculators, setSavedOPMCalculators] = useState(() =>
    indexOPMCalculatorsById(valuation.opm_calculators)
  );

  useEffect(() => {
    if (!arePendingChanges) {
      setSavedOPMCalculators(indexOPMCalculatorsById(valuation.opm_calculators));
    }
  }, [arePendingChanges, valuation.opm_calculators]);

  /** Tabs to mark with `*`: rows never saved (no pk yet) and rows that differ from their save. */
  const unsavedOPMCalculatorPanelIds = useMemo(
    () =>
      new Set(
        (valuation.opm_calculators ?? [])
          .filter(opmCalculator => {
            const saved = opmCalculator.id ? savedOPMCalculators.get(opmCalculator.id) : undefined;
            return !saved || hasOPMCalculatorChanged(saved, opmCalculator, opmCalculatorDecimalPlaces);
          })
          .map(getOPMCalculatorPanelId)
      ),
    [opmCalculatorDecimalPlaces, savedOPMCalculators, valuation.opm_calculators]
  );

  const updateValuationOPMCalculators = useCallback(
    (updated: OPMCalculator[]) => {
      const updatedValuation = {
        ...valuationRef.current,
        opm_calculators: updated,
      } as ValuationByAllocation;

      dispatch?.(valuationsAction.setValuationInfo(updatedValuation));
      setCurrentValuation(updatedValuation);
      setValuation(updatedValuation);
    },
    [dispatch, setCurrentValuation, setValuation]
  );

  // Local until Save: the valuation PUT sends the whole array, so an omitted row is deleted
  // server-side. Mirrors the calibration and range-sensitivity flows.
  const handleDeleteOPMCalculator = useCallback(
    (panelIdParam: string) => {
      if (panelIdParam) {
        const remaining = (valuationRef.current.opm_calculators ?? []).filter(
          opmCalculator => getOPMCalculatorPanelId(opmCalculator) !== panelIdParam
        );

        updateValuationOPMCalculators(remaining);

        setPageStatus(VALIDATED);
        setArePendingChanges(true);

        setTimeout(() => {
          setApproachQueryParam(VALUATION_SUMMARY_KEY);
        }, REDIRECT_TIMEOUT_DELAY);
      }
    },
    [
      updateValuationOPMCalculators,
      setPageStatus,
      setArePendingChanges,
      REDIRECT_TIMEOUT_DELAY,
      setApproachQueryParam,
    ]
  );

  const handleUpdateOPMCalculatorDialog = useCallback(
    (panelIdParam: string) => {
      if (panelIdParam) {
        handleMenuClose();

        const toUpdate = valuation.opm_calculators?.find(
          opmCalculator => getOPMCalculatorPanelId(opmCalculator) === panelIdParam
        );

        if (toUpdate) {
          setApproachToUpdate(null);
          setCalibrationToUpdate(null);
          setRangeSensitivityToUpdate(null);
          handleOpenUpdateApproachDialog();
          setOPMCalculatorToUpdate(toUpdate);
        }
      }
    },
    [
      handleMenuClose,
      handleOpenUpdateApproachDialog,
      setApproachToUpdate,
      setCalibrationToUpdate,
      setRangeSensitivityToUpdate,
      valuation.opm_calculators,
    ]
  );

  // The shared ApproachDialog shows one name field; these flags decide which.
  const showUpdateOPMCalculatorNameDialog = useCallback(
    (panelIdParam: string) => {
      if (panelIdParam) {
        setIsEditingApproachName(false);
        setIsEditingCalibrationName(false);
        setIsEditingRangeSensitivityName(false);
        setIsEditingOPMCalculatorName(true);
        setIsChangingFinancialStatement(false);
        handleUpdateOPMCalculatorDialog(panelIdParam);
      }
    },
    [
      handleUpdateOPMCalculatorDialog,
      setIsEditingApproachName,
      setIsEditingCalibrationName,
      setIsEditingRangeSensitivityName,
      setIsEditingOPMCalculatorName,
      setIsChangingFinancialStatement,
    ]
  );

  const handleUpdateOPMCalculatorName = useCallback(
    (panelIdParam: string, updatedName: string) => {
      const opmCalculators = valuationRef.current.opm_calculators ?? [];
      const index = opmCalculators.findIndex(
        opmCalculator => getOPMCalculatorPanelId(opmCalculator) === panelIdParam
      );

      if (index < 0) {
        return;
      }

      const updated = opmCalculators.map((opmCalculator, currentIndex) =>
        currentIndex === index
          ? { ...opmCalculator, name: getStringValue(updatedName) }
          : opmCalculator
      );

      updateValuationOPMCalculators(updated);

      setPageStatus(VALIDATED);
      setArePendingChanges(true);
    },
    [updateValuationOPMCalculators, setPageStatus, setArePendingChanges]
  );

  /**
   * Mirrors the calculator's edited inputs onto the valuation so the existing PUT persists
   * them. Local until Save, exactly like a rename.
   */
  const handleUpdateOPMCalculatorInputs = useCallback(
    (panelIdParam: string, inputs: Partial<OPMCalculatorInputs>) => {
      const opmCalculators = valuationRef.current.opm_calculators ?? [];
      const index = opmCalculators.findIndex(
        opmCalculator => getOPMCalculatorPanelId(opmCalculator) === panelIdParam
      );

      if (index < 0) {
        return;
      }

      const patch = buildOPMCalculatorInputsPatch(inputs);

      const updated = opmCalculators.map((opmCalculator, currentIndex) =>
        currentIndex === index ? { ...opmCalculator, ...patch } : opmCalculator
      );

      updateValuationOPMCalculators(updated);

      setPageStatus(VALIDATED);
      setArePendingChanges(true);
    },
    [updateValuationOPMCalculators, setPageStatus, setArePendingChanges]
  );

  /**
   * Opens a calculator prefilled from an OPM input surface (Equity Allocation, Backsolve,
   * Future Exit) -- the "Show OPM Calculations" links.
   *
   * An existing calculator is never overwritten. If one already prices the same inputs, the link
   * just focuses its tab; otherwise it opens a new calculator. A created row is client-side only
   * (uuid `panelId`) until Save, exactly like `handleAddApproach`'s OPM branch, which this
   * mirrors.
   *
   * The linked values are written onto the new row, not just into the query string: the row is
   * what Save persists, and the URL params are dropped on the next tab switch. The URL is still
   * written so the filled-in calculator stays shareable.
   */
  const handleOpenOPMCalculator = useCallback(
    (inputs: Partial<OPMCalculatorUrlInputs>, sourceLabel?: string) => {
      const opmCalculators = valuationRef.current.opm_calculators ?? [];
      const matchingOPMCalculator = opmCalculators.find(opmCalculator =>
        pricesSameOPMInputs(opmCalculator, inputs, opmCalculatorDecimalPlaces)
      );

      if (matchingOPMCalculator) {
        // The row's own values, not the link's: they are what the tab already shows, and a
        // param left over from another calculator must not win over them.
        openCalculatorTab(
          valuationTabId('opm_calculator', getOPMCalculatorPanelId(matchingOPMCalculator)),
          getOPMCalculatorUrlInputs(matchingOPMCalculator, opmCalculatorDecimalPlaces)
        );
        return;
      }

      // Every input the source lacks is left empty. A missing risk-free rate is backfilled by the
      // panel's FRED lookup from the linked maturity.
      const patch = buildOPMCalculatorInputsPatch({
        capTableId: inputs.capTableId,
        equityValue: inputs.equityValue,
        riskFreeRate: inputs.riskFreeRate,
        maturity: inputs.maturity,
        volatility: inputs.volatility,
        opmDate: inputs.opmDate,
      });
      const panelId = uuid();

      // Not a ValuationApproach on purpose: no `registerNode` and no weights, which is what
      // keeps the calculator out of valuation weighting and the summary.
      updateValuationOPMCalculators([
        ...opmCalculators,
        {
          name: OPM_CALCULATOR_MODEL_NAME,
          panelId,
          panel_id: panelId,
          valuation: getNumberValue(valuationRef.current.id),
          ...patch,
        } as unknown as OPMCalculator,
      ]);

      setPageStatus(VALIDATED);
      setArePendingChanges(true);

      setOPMCalculatorPrefillSource(sourceLabel ? { panelId, label: sourceLabel } : null);
      openCalculatorTab(valuationTabId('opm_calculator', panelId), inputs);
    },
    [
      opmCalculatorDecimalPlaces,
      openCalculatorTab,
      setArePendingChanges,
      setPageStatus,
      updateValuationOPMCalculators,
    ]
  );

  const clearOPMCalculatorPrefillSource = useCallback(() => setOPMCalculatorPrefillSource(null), []);

  return {
    clearOPMCalculatorPrefillSource,
    handleDeleteOPMCalculator,
    handleOpenOPMCalculator,
    handleUpdateOPMCalculatorInputs,
    handleUpdateOPMCalculatorName,
    showUpdateOPMCalculatorNameDialog,
    updateValuationOPMCalculators,
    setOPMCalculatorToUpdate,
    opmCalculatorToUpdate,
    opmCalculatorPrefillSource,
    setOPMCalculatorTabs,
    opmCalculatorTabs,
    unsavedOPMCalculatorPanelIds,
  };
};

export default useCreateOPMCalculator;
